import React from "react";
import { round } from "oc-commons/Maths";
import { I18N } from "oc-I18N";
import _ from "lodash";
import * as Maths3 from "oc-threejs/Maths3";
import { filterPathIntersectionCached } from "oc-app/metadata/GraphMetadata";
import { Box2 } from "three";
import { SizeInput } from "./SizeInput";
import { MenuItemSmallButton } from "./zauneditor/MenuItemSmallButton";
import { elementOrTor } from "../arrangement/elementFunctions";
import { MTor } from "../../metamodell";
import { PAuswahlwert, PMass, PObjekt, PTor } from "../../metamodell_paths";
import { ZElement } from "../arrangement/ZElement";

export const breitenEingabeLabel = (object: any) =>
  object.isEmpty
    ? I18N("Abstand")
    : object.isDoppeltor || object.isSchiebetor
      ? I18N("Lichte Weite")
      : I18N("Breite");

const minMaxBox = (object: any): Box2 => object?.sizeRange?.box ?? object.getMinMaxBox();

export function BreitenEingabe({ object }: { object: ZElement }) {
  const agt = APP.arrangement;
  const box = minMaxBox(object);
  const doppelTorSummand = object.isDoppeltor ? object.lichteWeite - object.breite : 0;
  return (
    <SizeInput
      key={object.id + ".x"}
      dataInputKey={breitenEingabeLabel(object)}
      axis="x"
      value={object.breite + doppelTorSummand}
      setter={(value: number) => {
        agt.selectedObjects.forEach((x) => x.setKonfigurierbareBreite(value - doppelTorSummand));
        agt.update3d();
        APP.logUserAction({ action: "resizeElement", variable: "breite", value });
      }}
      disabled={!object.isBreiteKonfigurierbar || !(box.min.x + 0.001 < box.max.x)}
      min={round(box.min.x + doppelTorSummand, 2)}
      max={round(box.max.x + doppelTorSummand, 2)}
    />
  );
}

export function HöhenEingabe({ object }: { object: ZElement }) {
  const agt = APP.arrangement;
  const box = minMaxBox(object);
  const doppelTorSummand = object.isDoppeltor ? object.lichteWeite - object.breite : 0;
  return (
    <SizeInput
      key={object.id + ".y"}
      dataInputKey="Höhe"
      axis="y"
      value={object.höhe}
      disabled={!object.isBreiteKonfigurierbar || !(box.min.y + 0.001 < box.max.y)}
      setter={(value: number) => {
        agt.selectedObjects.forEach((x) => (!x.isEmpty ? x.setKonfigurierbareHöhe(value) : null));
        agt.update3d();
        APP.logUserAction({ action: "resizeElement", variable: "höhe", value });
      }}
      min={box.min.y}
      max={box.max.y}
    />
  );
}

export function StandardMassInfoRow({ object }: { object: ZElement }) {
  const getMasseFürGleicheFormFüllung = (part: ZElement) => {
    const elementMetasForm = filterPathIntersectionCached("ZaunCircleMenuItemElementSize.tsx:85")(
      part.meta.Mass.Form,
      (p: PObjekt) => elementOrTor(p.Mass)
    ) as MTor[];
    const elementMetasFormFüllung = part.füllungAuswahlwert
      ? filterPathIntersectionCached("ZaunCircleMenuItemElementSize.tsx:89")(
          elementMetasForm,
          (p: PTor) => p,
          part.füllungAuswahlwert,
          (p: PAuswahlwert) => elementOrTor(p.Füllung_Objekt)
        )
      : [];
    return elementMetasFormFüllung.map((el) => el.Mass);
  };

  const setToStandardMass = () => {
    const part = APP.arrangement.selectedObjects[0];
    const masse = getMasseFürGleicheFormFüllung(part);
    const sorted = masse
      .map((mass) => ({
        mass,
        score:
          (mass.Variabel == "fix" ? 0 : mass.Variabel == "bauseits" ? 10 : 100) +
          mass.Standard.distanceTo(Maths3.v3To2xy(part.size)),
      }))
      .sort((a, b) => a.score - b.score);
    const bestMass = _.first(sorted)?.mass;
    let massStandard = (bestMass ?? part.meta.Mass).Standard;
    if (part.isDoppeltor) {
      part.setFlügelBreite(massStandard.x, 0);
      part.setFlügelBreite(massStandard.x, 1);
    } else {
      part.setKonfigurierbareBreite(massStandard.x);
    }
    part.setKonfigurierbareHöhe(massStandard.y);
    APP.dirty();
  };

  const AufStandardLink = () => {
    if (APP.arrangement.selectedObjects.length > 1) return null;

    const part = APP.arrangement.selectedObjects[0];
    const masse = getMasseFürGleicheFormFüllung(part);
    const einMassNichtFertigungExistiert = masse.some((mass) => mass.Variabel != "fertigung");
    if (!einMassNichtFertigungExistiert) {
      return null;
    } else {
      return (
        <MenuItemSmallButton onClick={setToStandardMass} className={"font-size-small"}>
          {I18N("Auf Standard wechseln")}
        </MenuItemSmallButton>
      );
    }
  };

  if (!object.isElement || object.isStandardMass) {
    return null;
  }
  return (
    <span className="text font-size-small italic" data-info-key="Variables Element">
      {object.meta.Mass.Variabel == "bauseits"
        ? I18N("muss bauseits auf dieses Mass gekürzt werden")
        : I18N("dieses Mass wird individuell angefertigt")}
      &nbsp;
      <AufStandardLink />
    </span>
  );
}
